import type { LocationQueryRaw, RouteLocationNormalizedLoaded } from 'vue-router'
import { isMediaSoundPreferenceEnabled } from '@/composables/useMediaSoundPreference'
import {
  getDreamaDomain,
  getSharedCookieDomain,
  getSharedRootDomain,
  normalizeDomainOrigin,
} from '@/constants/domain'
import { getEnvMode, type EnvMode } from '@/utils/env'

export const LEGACY_PLAYER_QUERY_KEY = 'legacyPlayerUrl'
export const LEGACY_PLAYER_BRIDGE_QUERY_KEY = 'legacyPlayerBridge'
export const LEGACY_PLAYER_OPEN_EVENT = 'dreama:open-legacy-player'
export const LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE = 'dreama:legacy-player-bridge-auth'
export const LEGACY_PLAYER_BRIDGE_AUTH_REQUEST = 'getUserInfo'
export const LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE = 'userInfo'
export const LEGACY_PLAYER_DEBUG_QUERY_KEY = 'legacyPlayerDebug'
export const LEGACY_PLAYER_SOUND_COOKIE_NAME = 'isPlay'

const LEGACY_PLAYER_ORIGINS: Record<EnvMode, string> = {
  development: 'https://ciyuan-t.ideaflow.pro',
  test: 'https://ciyuan-t.ideaflow.pro',
  uat: 'https://dreama-uat.ideaflow.pro',
  production: 'https://dreama.ideaflow.pro',
}
const LEGACY_PLAYER_BRIDGE_ORIGINS: Partial<Record<EnvMode, string>> = {
  production: 'https://web.ideaflow.pro',
}
const LEGACY_PLAYER_PRODUCTION_BRIDGE_PARENT_HOSTS = new Set(['dreama.fun', 'www.dreama.fun'])
const LEGACY_PLAYER_IFRAME_HOSTS = new Set([
  'ciyuan-t.ideaflow.pro',
  'dreama.ideaflow.pro',
  'dreama-uat.ideaflow.pro',
  'dreama.fun',
  'www.dreama.fun',
])
const LEGACY_PLAYER_ALLOWED_HOSTS = new Set([
  'ciyuan-t.ideaflow.pro',
  'dreama.ideaflow.pro',
  'dreama-uat.ideaflow.pro',
  'test.dreama.fun',
  'uat.dreama.fun',
  'dreama.fun',
  'www.dreama.fun',
  'localhost',
  '127.0.0.1',
  '[::1]',
])
const LEGACY_PLAYER_ALLOWED_PATHS = new Set([
  '/view.html',
  '/preview.html',
  '/radio.html',
  '/rolematerial.html',
])
const LEGACY_PLAYER_SOUND_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7

export interface LegacyPlayerOpenDetail {
  url: string
}

export interface LegacyPlayerOpenOptions {
  forceAutoplaySound?: boolean
}

export interface LegacyPlayerOverlayDecisionOptions {
  currentOrigin?: string
  currentQuery?: LocationQueryRaw
  mode?: EnvMode
}

export interface LegacyPlayerFrameUrlOptions {
  currentOrigin?: string
  mode?: EnvMode
}

export interface LegacyPlayerBridgeAuthMessage {
  type: typeof LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE
  id: string
  action: typeof LEGACY_PLAYER_BRIDGE_AUTH_REQUEST | typeof LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE
  data?: unknown
}

declare global {
  interface WindowEventMap {
    [LEGACY_PLAYER_OPEN_EVENT]: CustomEvent<LegacyPlayerOpenDetail>
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isLocalLegacyPlayerHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
}

function getCurrentOrigin(currentOrigin?: string): string {
  return currentOrigin ?? (typeof window === 'undefined' ? '' : window.location.origin)
}

export function getLegacyPlayerOrigin(mode: EnvMode = getEnvMode()): string {
  const modeOverride =
    mode === 'production'
      ? import.meta.env.VITE_LEGACY_PLAYER_ORIGIN ||
        import.meta.env.VITE_LEGACY_PLAYER_PRODUCTION_ORIGIN
      : mode === 'uat'
        ? import.meta.env.VITE_LEGACY_PLAYER_UAT_ORIGIN
        : mode === 'test'
          ? import.meta.env.VITE_LEGACY_PLAYER_TEST_ORIGIN
          : import.meta.env.VITE_LEGACY_PLAYER_DEVELOPMENT_ORIGIN
  const override = normalizeDomainOrigin(modeOverride)

  if (override) {
    return override
  }

  return LEGACY_PLAYER_ORIGINS[mode]
}

export function getLegacyPlayerBridgeOrigin(mode: EnvMode = getEnvMode()): string | null {
  const modeOverride =
    mode === 'production'
      ? import.meta.env.VITE_LEGACY_PLAYER_BRIDGE_ORIGIN ||
        import.meta.env.VITE_LEGACY_PLAYER_BRIDGE_PRODUCTION_ORIGIN
      : mode === 'uat'
        ? import.meta.env.VITE_LEGACY_PLAYER_BRIDGE_UAT_ORIGIN
        : mode === 'test'
          ? import.meta.env.VITE_LEGACY_PLAYER_BRIDGE_TEST_ORIGIN
          : import.meta.env.VITE_LEGACY_PLAYER_BRIDGE_DEVELOPMENT_ORIGIN
  const override = normalizeDomainOrigin(modeOverride)

  if (override) {
    return override
  }

  return LEGACY_PLAYER_BRIDGE_ORIGINS[mode] ?? null
}

export function isLegacyPlayerBridgeAuthMessage(
  value: unknown,
): value is LegacyPlayerBridgeAuthMessage {
  if (!isRecord(value)) {
    return false
  }

  return (
    value.type === LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE &&
    typeof value.id === 'string' &&
    (value.action === LEGACY_PLAYER_BRIDGE_AUTH_REQUEST ||
      value.action === LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE)
  )
}

export function isAllowedLegacyPlayerBridgeParentOrigin(
  origin: string,
  mode: EnvMode = getEnvMode(),
): boolean {
  try {
    const parentUrl = new URL(origin)

    if (parentUrl.protocol === 'https:') {
      if (mode === 'production') {
        return LEGACY_PLAYER_PRODUCTION_BRIDGE_PARENT_HOSTS.has(parentUrl.hostname)
      }

      return getBrandRootDomain(parentUrl.hostname) === 'dreama.fun'
    }

    return (
      mode !== 'production' &&
      ['http:', 'https:'].includes(parentUrl.protocol) &&
      isLocalLegacyPlayerHost(parentUrl.hostname)
    )
  } catch {
    return false
  }
}

function buildLegacyPlayerUrlWithOrigin(parsed: URL, origin: string): string {
  const remoteUrl = new URL(parsed.pathname, origin)
  remoteUrl.search = parsed.search
  remoteUrl.hash = parsed.hash

  return remoteUrl.toString()
}

function buildEnvironmentLegacyPlayerUrl(parsed: URL, mode: EnvMode): string {
  return buildLegacyPlayerUrlWithOrigin(parsed, getLegacyPlayerOrigin(mode))
}

function getBrandRootDomain(hostname: string): string | null {
  return getSharedRootDomain(hostname)
}

function shouldUseDreamaDomainPlayer(currentOrigin: string, mode: EnvMode): boolean {
  if (mode !== 'production' || !currentOrigin) {
    return false
  }

  try {
    const currentUrl = new URL(currentOrigin)
    return (
      currentUrl.protocol === 'https:' &&
      LEGACY_PLAYER_PRODUCTION_BRIDGE_PARENT_HOSTS.has(currentUrl.hostname)
    )
  } catch {
    return false
  }
}

function applyDreamaDomainPlayerDefaults(url: string): string {
  const playerUrl = new URL(url)

  if (playerUrl.pathname === '/rolematerial.html' && !playerUrl.searchParams.has('play')) {
    playerUrl.searchParams.set('play', '1')
  }

  return playerUrl.toString()
}

function isAllowedIframeParentRoot(rootDomain: string | null): boolean {
  return rootDomain === 'ideaflow.pro' || rootDomain === 'dreama.fun'
}

function shouldBridgeIframeParentRoot(rootDomain: string | null): boolean {
  return rootDomain === 'dreama.fun'
}

function isLocalCurrentPageOrigin(currentOrigin: string): boolean {
  if (!currentOrigin) {
    return false
  }

  try {
    return isLocalLegacyPlayerHost(new URL(currentOrigin).hostname)
  } catch {
    return false
  }
}

function isTruthyFlagValue(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function isTruthyQueryFlagValue(value: LocationQueryRaw[string]): boolean {
  if (Array.isArray(value)) {
    return value.some(isTruthyFlagValue)
  }

  return isTruthyFlagValue(value)
}

function appendLocationQueryValue(query: LocationQueryRaw, key: string, value: string) {
  const currentValue = query[key]

  if (Array.isArray(currentValue)) {
    query[key] = [...currentValue, value]
    return
  }

  if (currentValue !== undefined) {
    query[key] = [currentValue, value]
    return
  }

  query[key] = value
}

function appendSearchParamsToQuery(query: LocationQueryRaw, value: string) {
  const rawValue = value.trim().replace(/^[?#]/, '')

  if (!rawValue) {
    return
  }

  const params = new URLSearchParams(rawValue)

  params.forEach((paramValue, key) => {
    appendLocationQueryValue(query, key, paramValue)
  })
}

export function parseLegacyPlayerLocationQuery(search = '', hash = ''): LocationQueryRaw {
  const query: LocationQueryRaw = {}
  appendSearchParamsToQuery(query, search)

  const rawHash = hash.trim().replace(/^#/, '')
  const hashQuery = rawHash.includes('?') ? rawHash.slice(rawHash.indexOf('?') + 1) : rawHash

  appendSearchParamsToQuery(query, hashQuery)

  return query
}

export function getLegacyPlayerCurrentLocationQuery(): LocationQueryRaw {
  if (typeof window === 'undefined') {
    return {}
  }

  return parseLegacyPlayerLocationQuery(window.location.search, window.location.hash)
}

export function getLegacyPlayerQueryString(
  currentQuery: LocationQueryRaw,
  key: string,
): string | null {
  const value = currentQuery[key]

  if (Array.isArray(value)) {
    const firstValue = value[0]
    return typeof firstValue === 'string' ? firstValue : null
  }

  return typeof value === 'string' ? value : null
}

export function isLegacyPlayerBridgeMode(currentQuery: LocationQueryRaw = {}): boolean {
  return isTruthyQueryFlagValue(currentQuery[LEGACY_PLAYER_BRIDGE_QUERY_KEY])
}

export function shouldRenderLegacyPlayerBridgeMode(
  currentQuery: LocationQueryRaw = {},
  currentOrigin = getCurrentOrigin(),
): boolean {
  if (!isLegacyPlayerBridgeMode(currentQuery) || !currentOrigin) {
    return false
  }

  try {
    const currentUrl = new URL(currentOrigin)
    return (
      currentUrl.protocol === 'https:' && getBrandRootDomain(currentUrl.hostname) === 'ideaflow.pro'
    )
  } catch {
    return false
  }
}

export function isLegacyPlayerOverlayDebugEnabled(
  currentQuery: LocationQueryRaw = {},
  mode: EnvMode = getEnvMode(),
  currentOrigin = getCurrentOrigin(),
): boolean {
  const hasDebugFlag =
    isTruthyQueryFlagValue(currentQuery[LEGACY_PLAYER_DEBUG_QUERY_KEY]) ||
    isTruthyFlagValue(import.meta.env.VITE_LEGACY_PLAYER_FORCE_OVERLAY)

  if (!hasDebugFlag) {
    return false
  }

  if (mode !== 'production') {
    return true
  }

  return isLocalCurrentPageOrigin(currentOrigin)
}

export function shouldUseLegacyPlayerBridge({
  currentOrigin = getCurrentOrigin(),
  mode = getEnvMode(),
}: LegacyPlayerFrameUrlOptions = {}): boolean {
  const bridgeOrigin = getLegacyPlayerBridgeOrigin(mode)

  if (!bridgeOrigin || !currentOrigin) {
    return false
  }

  if (shouldUseDreamaDomainPlayer(currentOrigin, mode)) {
    return false
  }

  try {
    const currentUrl = new URL(currentOrigin)
    const bridgeUrl = new URL(bridgeOrigin)
    const currentRootDomain = getBrandRootDomain(currentUrl.hostname)

    return (
      currentUrl.protocol === 'https:' &&
      bridgeUrl.protocol === 'https:' &&
      shouldBridgeIframeParentRoot(currentRootDomain)
    )
  } catch {
    return false
  }
}

export function canEmbedLegacyPlayerInCurrentPage(
  playerUrl: string | null | undefined,
  currentOrigin = getCurrentOrigin(),
): boolean {
  const normalizedPlayerUrl = resolveLegacyPlayerDirectUrl(playerUrl, { currentOrigin })

  if (!normalizedPlayerUrl || !currentOrigin) {
    return false
  }

  try {
    const currentUrl = new URL(currentOrigin)
    const player = new URL(normalizedPlayerUrl)
    const currentRootDomain = getBrandRootDomain(currentUrl.hostname)

    return (
      currentUrl.protocol === 'https:' &&
      player.protocol === 'https:' &&
      isAllowedIframeParentRoot(currentRootDomain) &&
      LEGACY_PLAYER_IFRAME_HOSTS.has(player.hostname)
    )
  } catch {
    return false
  }
}

export function shouldOpenLegacyPlayerInOverlay(
  playerUrl: string | null | undefined,
  options: LegacyPlayerOverlayDecisionOptions = {},
): boolean {
  const currentOrigin = getCurrentOrigin(options.currentOrigin)

  if (canEmbedLegacyPlayerInCurrentPage(playerUrl, currentOrigin)) {
    return true
  }

  return (
    isLegacyPlayerOverlayDebugEnabled(options.currentQuery, options.mode, currentOrigin) &&
    normalizeLegacyPlayerUrl(playerUrl) !== null
  )
}

export function hasLegacyPlayerSoundIntentCookie(
  cookie = typeof document === 'undefined' ? '' : document.cookie,
): boolean {
  return cookie.split(';').some((entry) => {
    const [rawName, ...rawValueParts] = entry.trim().split('=')
    let value = rawValueParts.join('=')

    try {
      value = decodeURIComponent(value)
    } catch {
      // Ignore malformed cookie values from unrelated scripts.
    }

    return rawName === LEGACY_PLAYER_SOUND_COOKIE_NAME && ['true', '1'].includes(value)
  })
}

function getLegacyPlayerSoundIntentCookieDomain(hostname: string): string | undefined {
  const normalizedHostname = hostname.trim().toLowerCase().replace(/\.$/, '')
  const sharedCookieDomain = getSharedCookieDomain(hostname)

  if (sharedCookieDomain) {
    return sharedCookieDomain
  }

  if (normalizedHostname === 'dreama.fun' || normalizedHostname === 'www.dreama.fun') {
    return '.dreama.fun'
  }

  return undefined
}

export function buildLegacyPlayerSoundIntentCookie(
  currentOrigin = getCurrentOrigin(),
): string | null {
  if (!currentOrigin) {
    return null
  }

  try {
    const currentUrl = new URL(currentOrigin)
    const isSecureOrigin = currentUrl.protocol === 'https:'
    const isLocalOrigin =
      ['http:', 'https:'].includes(currentUrl.protocol) &&
      isLocalLegacyPlayerHost(currentUrl.hostname)

    if (!isSecureOrigin && !isLocalOrigin) {
      return null
    }

    const cookieDomain = getLegacyPlayerSoundIntentCookieDomain(currentUrl.hostname)
    const attributes = [
      `${LEGACY_PLAYER_SOUND_COOKIE_NAME}=true`,
      `Max-Age=${LEGACY_PLAYER_SOUND_COOKIE_MAX_AGE_SECONDS}`,
      'Path=/',
    ]

    if (cookieDomain) {
      attributes.push(`Domain=${cookieDomain}`)
    }

    attributes.push('SameSite=Lax')

    if (isSecureOrigin) {
      attributes.push('Secure')
    }

    return attributes.join('; ')
  } catch {
    return null
  }
}

export function markLegacyPlayerSoundIntent(currentOrigin = getCurrentOrigin()): boolean {
  if (typeof document === 'undefined') {
    return false
  }

  const cookie = buildLegacyPlayerSoundIntentCookie(currentOrigin)

  if (!cookie) {
    return false
  }

  document.cookie = cookie
  return true
}

function hasLegacyPlayerAutoplaySoundIntent(
  url: string,
  options: LegacyPlayerOpenOptions,
): boolean {
  if (options.forceAutoplaySound || isMediaSoundPreferenceEnabled()) {
    return true
  }

  try {
    const playerUrl = new URL(url)

    return playerUrl.pathname === '/rolematerial.html' && playerUrl.searchParams.get('play') === '1'
  } catch {
    return false
  }
}

export function applyLegacyPlayerSoundPreference(
  url: string,
  options: LegacyPlayerOpenOptions = {},
): string {
  if (!hasLegacyPlayerAutoplaySoundIntent(url, options)) {
    return url
  }

  markLegacyPlayerSoundIntent()

  try {
    const playerUrl = new URL(url)

    if (playerUrl.pathname === '/rolematerial.html') {
      playerUrl.searchParams.set('play', '1')
    }

    return playerUrl.toString()
  } catch {
    return url
  }
}

export function normalizeLegacyPlayerUrl(
  url: string | null | undefined,
  mode: EnvMode = getEnvMode(),
): string | null {
  const rawUrl = url?.trim()

  if (!rawUrl) {
    return null
  }

  try {
    const parsed = new URL(rawUrl)
    const isAllowedHost = LEGACY_PLAYER_ALLOWED_HOSTS.has(parsed.hostname)
    const isAllowedPath = LEGACY_PLAYER_ALLOWED_PATHS.has(parsed.pathname)
    const isLocalHost = isLocalLegacyPlayerHost(parsed.hostname)
    const isAllowedProtocol = parsed.protocol === 'https:' || isLocalHost

    if (!isAllowedHost || !isAllowedPath || !isAllowedProtocol) {
      return null
    }

    return buildEnvironmentLegacyPlayerUrl(parsed, mode)
  } catch {
    return null
  }
}

export function resolveLegacyPlayerDirectUrl(
  url: string | null | undefined,
  options: LegacyPlayerFrameUrlOptions = {},
): string | null {
  const mode = options.mode ?? getEnvMode()
  const normalizedPlayerUrl = normalizeLegacyPlayerUrl(url, mode)

  if (!normalizedPlayerUrl) {
    return null
  }

  const currentOrigin = getCurrentOrigin(options.currentOrigin)

  if (!shouldUseDreamaDomainPlayer(currentOrigin, mode)) {
    return normalizedPlayerUrl
  }

  return applyDreamaDomainPlayerDefaults(
    buildLegacyPlayerUrlWithOrigin(new URL(normalizedPlayerUrl), getDreamaDomain(mode)),
  )
}

export function isLegacyPlayerUrl(url: string | null | undefined): boolean {
  return normalizeLegacyPlayerUrl(url) !== null
}

function addLegacyPlayerBridgeMarker(url: string): string {
  const playerUrl = new URL(url)
  const hasBridgeMarker = Array.from(playerUrl.searchParams.entries()).some(
    ([key, value]) => key === 'studio' || value === 'studio',
  )

  if (!hasBridgeMarker) {
    playerUrl.searchParams.set('studio', '1')
  }

  return playerUrl.toString()
}

function buildLegacyPlayerBridgeParams(normalizedPlayerUrl: string): URLSearchParams {
  const params = new URLSearchParams()
  params.set(LEGACY_PLAYER_BRIDGE_QUERY_KEY, '1')
  params.set(LEGACY_PLAYER_QUERY_KEY, addLegacyPlayerBridgeMarker(normalizedPlayerUrl))

  return params
}

export function buildLegacyPlayerBridgeUrl(
  url: string | null | undefined,
  mode: EnvMode = getEnvMode(),
): string | null {
  const normalizedPlayerUrl = normalizeLegacyPlayerUrl(url, mode)
  const bridgeOrigin = getLegacyPlayerBridgeOrigin(mode)

  if (!normalizedPlayerUrl || !bridgeOrigin) {
    return null
  }

  const bridgeUrl = new URL('/', bridgeOrigin)
  const bridgeParams = buildLegacyPlayerBridgeParams(normalizedPlayerUrl)
  bridgeUrl.search = bridgeParams.toString()
  bridgeUrl.hash = bridgeParams.toString()

  return bridgeUrl.toString()
}

export function resolveLegacyPlayerFrameUrl(
  url: string | null | undefined,
  options: LegacyPlayerFrameUrlOptions = {},
): string | null {
  const mode = options.mode ?? getEnvMode()
  const normalizedPlayerUrl = resolveLegacyPlayerDirectUrl(url, {
    currentOrigin: options.currentOrigin,
    mode,
  })

  if (!normalizedPlayerUrl) {
    return null
  }

  if (!shouldUseLegacyPlayerBridge({ currentOrigin: options.currentOrigin, mode })) {
    return normalizedPlayerUrl
  }

  return buildLegacyPlayerBridgeUrl(normalizedPlayerUrl, mode) ?? normalizedPlayerUrl
}

export function buildLegacyPlayerRouteQuery(
  url: string,
  currentQuery: LocationQueryRaw,
): LocationQueryRaw {
  return {
    ...currentQuery,
    [LEGACY_PLAYER_QUERY_KEY]: url,
  }
}

export function removeLegacyPlayerRouteQuery(currentQuery: LocationQueryRaw): LocationQueryRaw {
  const nextQuery = { ...currentQuery }
  delete nextQuery[LEGACY_PLAYER_QUERY_KEY]
  delete nextQuery[LEGACY_PLAYER_BRIDGE_QUERY_KEY]
  return nextQuery
}

function serializeRouteKeyQuery(currentQuery: LocationQueryRaw): string {
  const filteredQuery = removeLegacyPlayerRouteQuery(currentQuery)
  const keys = Object.keys(filteredQuery).sort()

  if (keys.length === 0) {
    return ''
  }

  return JSON.stringify(
    keys.map((key) => {
      const value = filteredQuery[key]
      return [key, Array.isArray(value) ? [...value] : value]
    }),
  )
}

export function buildRouteKeyWithoutLegacyPlayerQuery(
  route: Pick<RouteLocationNormalizedLoaded, 'path' | 'query' | 'hash'>,
): string {
  const queryKey = serializeRouteKeyQuery(route.query)
  return [route.path, queryKey, route.hash].join('|')
}

export function openLegacyPlayerUrl(
  url: string | null | undefined,
  options: LegacyPlayerOpenOptions = {},
): boolean {
  const normalizedUrl = resolveLegacyPlayerDirectUrl(url)

  if (!normalizedUrl || typeof window === 'undefined') {
    return false
  }

  const playerUrl = applyLegacyPlayerSoundPreference(normalizedUrl, options)
  const event = new CustomEvent<LegacyPlayerOpenDetail>(LEGACY_PLAYER_OPEN_EVENT, {
    cancelable: true,
    detail: {
      url: playerUrl,
    },
  })

  window.dispatchEvent(event)

  if (!event.defaultPrevented) {
    window.open(playerUrl, '_blank', 'noopener,noreferrer')
  }

  return true
}
